/**
 * Header Component — position: fixed + scroll-up reveal.
 */
import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';

class HeaderComponent extends Component {
  #prevY = 0;
  #rafId = null;
  #height = 64;
  #hidden = false;
  #isTransparent = false;
  #aboveHeight = 0;
  #group = null;

  #ro = new ResizeObserver(() => this.#measure());
  #groupPointerTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#ro.observe(this);
    this.#isTransparent = this.hasAttribute('data-transparent');
    this.#observeSiblings();
    this.#measure();
    this.#prevY = window.scrollY;
    this.#group = document.querySelector('#header-group');

    // Pendant l'animation translateY, le header intercepterait les clics du contenu : pointer-events coupés.
    this.#group?.addEventListener('transitionstart', this.#onGroupTransitionStart);
    this.#group?.addEventListener('transitionend', this.#onGroupTransitionEnd);
    this.#group?.addEventListener('transitioncancel', this.#onGroupTransitionEnd);

    if (this.getAttribute('sticky') === 'scroll-up') {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ro.disconnect();
    window.removeEventListener('scroll', this.#onScroll);
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#group?.removeEventListener('transitionstart', this.#onGroupTransitionStart);
    this.#group?.removeEventListener('transitionend', this.#onGroupTransitionEnd);
    this.#group?.removeEventListener('transitioncancel', this.#onGroupTransitionEnd);
    clearTimeout(this.#groupPointerTimer);
    document.querySelector('.header-spacer')?.remove();
  }

  #onGroupTransitionStart = (event) => {
    if (event.target !== this.#group || event.propertyName !== 'transform') return;
    this.#group.style.pointerEvents = 'none';
    clearTimeout(this.#groupPointerTimer);
    // Filet de sécurité si transitionend ne fire jamais (display, unload…)
    this.#groupPointerTimer = setTimeout(this.#restoreGroupPointer, 1000);
  };

  #onGroupTransitionEnd = (event) => {
    if (event.target !== this.#group || event.propertyName !== 'transform') return;
    this.#restoreGroupPointer();
  };

  #restoreGroupPointer = () => {
    clearTimeout(this.#groupPointerTimer);
    if (this.#group) this.#group.style.pointerEvents = '';
  };

  #observeSiblings() {
    const group = document.querySelector('#header-group');
    if (!group) return;
    for (const child of group.children) {
      if (child instanceof HTMLElement && !child.contains(this)) {
        this.#ro.observe(child);
      }
    }
  }

  #measure() {
    this.#height = this.offsetHeight || 64;

    const group = document.querySelector('#header-group');
    if (group) {
      let above = 0;
      for (const child of group.children) {
        if (child instanceof HTMLElement && child.contains(this)) break;
        above += child.offsetHeight || 0;
      }
      this.#aboveHeight = above;
    }

    document.body.style.setProperty('--header-height', `${this.#aboveHeight + this.#height}px`);
    this.#updateSpacer();
  }

  #updateSpacer() {
    const group = document.querySelector('#header-group');
    if (!group) return;
    let spacer = document.querySelector('.header-spacer');
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.className = 'header-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      group.after(spacer);
    }
    spacer.style.height = `${this.#aboveHeight + this.#height}px`;
  }

  #onScroll = () => {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#tick();
    });
  };

  #tick() {
    // Ignore scroll events fired by lockScroll()/unlockScroll() (body position: fixed)
    if (document.body.style.position === 'fixed') return;

    const y = window.scrollY;
    const dy = y - this.#prevY;
    this.#prevY = y;

    if (this.#isTransparent) {
      const past = y > this.#height;
      this.classList.toggle('header--transparent', !past);
      this.classList.toggle('header--solid', past);
    }

    if (Math.abs(dy) < 2) return;

    if (y <= 5) {
      this.#show();
      this.dataset.stuck = 'false';
      return;
    }

    // Hide immediately on scroll down (threshold = 10px from top)
    if (dy > 0 && y > 10) {
      this.#hide();
      this.dataset.stuck = 'true';
    } else if (dy < 0) {
      this.#show();
      this.dataset.stuck = 'true';
    }
  }

  #hide() {
    if (this.#hidden) return;
    this.#hidden = true;
    this.#group?.classList.add('header-group--hidden');
  }

  #show() {
    if (!this.#hidden) return;
    this.#hidden = false;
    this.#group?.classList.remove('header-group--hidden');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const group = document.querySelector('#header-group');
  if (!group) return;
  const ro = new ResizeObserver((entries) => {
    let h = 0;
    for (const e of entries) h += e.borderBoxSize[0]?.blockSize ?? 0;
    document.body.style.setProperty('--header-group-height', `${Math.round(h)}px`);
  });
  for (const child of group.children) {
    if (child instanceof HTMLElement) ro.observe(child);
  }
});